import type {
  ApiNode,
  Gpu,
  GpuBind,
  GpuExclusion,
  NodeProgress,
  NodeStage,
  NodeBootLog,
  NodeClusterMembershipState,
  NodeImageCopy,
  NodeValidation,
  NodeValidationCheckCode,
  NodeStepName,
  ValidationStep,
  VerifyFailureCode,
  VerifyRun,
  VerifyStepName,
} from '../api/types'
import type { Tone } from './lease'
import { ISO_WORD } from './iso'
import { DASH, elapsed } from '../ui/format'

// 백엔드의 상태 기계를 화면의 낱말로 옮기는 층. 순수 함수만 둔다 — 여기가 틀리면 화면 전체가 틀린다.

export type Status = 'ready' | 'preparing' | 'paused' | 'offline' | 'awaiting' | 'failed'

const PREPARING = new Set([
  'enrolling',
  'installed',
  'vfio_configured',
  'gpu_verified',
  'joining',
  'joined',
  'burn_in',
])

/**
 * 노드 state 를 화면이 쓰는 여섯 상태로 줄인다. 원 state 는 화면 어디에도 나오지 않는다.
 * 온보딩 중인 노드는 heartbeat 가 없어도 오프라인이 아니다 — 설치·재부팅이 그 국면의 일부다.
 * 걸음 원장이 재부팅과 죽음을 가르므로(#392), 걸음에서 멈춘 노드만 오프라인이다:
 * 백엔드의 응답을 기다리는 노드는 heartbeat 를 보내는 동안 그 걸음이 열린 채라 준비 중이다.
 * 설치가 끝났는데 관리 주소로 닿지 않는 노드(#489)도 준비 중이 아니라 오프라인이다.
 * 클러스터가 보는 노드라도 보고가 끊겼거나 재부팅 뒤 돌아왔다고 하지 않으면(#503) 사용 가능이 아니라 오프라인이다.
 */
export function status(node: ApiNode): Status {
  if (node.state === 'onboarding_failed') return 'failed'
  if (unreportedWord(node)) return 'offline'
  if (PREPARING.has(node.state)) return halted(node.progress) ? 'offline' : 'preparing'
  if (!node.online) return 'offline'
  if (node.state === 'ready') {
    if (!node.schedulable) return 'paused'
    if (!node.cluster_id) return 'awaiting'
    return 'ready'
  }
  if (node.state === 'maintenance' || node.state === 'retired') return 'paused'
  return 'preparing'
}

/**
 * 노드의 보고가 끊긴 고장의 한 문장(#503) — 재부팅이 돌아오지 않은 것이 먼저다. 없으면 null.
 * 원인은 추측하지 않는다.
 */
export function unreportedWord(node: ApiNode): string | null {
  if (node.reboot_unreported_since) return '재부팅 뒤 돌아왔다고 보고하지 않습니다.'
  if (node.agent_silent_since) return '노드의 보고가 끊겼습니다.'
  return null
}

/**
 * 노드가 말한 클러스터 VM 망의 고장 한 문장(D-7). 설정하지 못한 것이 먼저다. 없으면 null.
 * 이 노드의 VM 은 닿지 않는 노드의 VM 과 통신하지 못한다.
 */
export function clusterNetFaultWord(node: ApiNode): string | null {
  const f = node.cluster_net_fault
  if (!f) return null
  if (f.error) return '이 노드의 VM 망을 설정하지 못했습니다.'
  return `다른 노드 ${f.unreachable_peers.length}대의 VM 망에 닿지 않습니다. 노드들이 관리 네트워크에서 서로 닿는지 확인하세요.`
}

/** 진행이 멈춘 노드 — 걸음에서 멈췄거나(stuck) 설치 뒤 닿지 않는다(unreachable, #489). 둘 다 사람이 가 봐야 한다. */
export function halted(progress: NodeProgress | null | undefined): boolean {
  return progress?.state === 'stuck' || progress?.state === 'unreachable'
}

export const statusLabel: Record<Status, string> = {
  ready: '사용 가능',
  preparing: '준비 중',
  paused: '점검 중',
  offline: '오프라인',
  awaiting: '편입 대기',
  failed: '설치 실패',
}

const membershipLabel: Record<NodeClusterMembershipState, string> = {
  not_ready: '준비 중',
  awaiting_decision: '연결 대기',
  decision_recorded: '변경 대기',
  joining: '연결 중',
  member: '연결됨',
  leaving: '연결 해제 중',
  detached: '미연결',
}

/**
 * 클러스터 소속 상태의 원값을 짧은 제품 낱말로만 보인다.
 * 연결 중인 노드는 결정이 생성이었는지 연결이었는지로 진행을 말한다.
 */
export function membershipWord(state: string, decision?: { action: string } | null): string {
  if (state === 'joining' && decision) {
    return decision.action === 'create' ? '생성 중' : '연결 중'
  }
  return (membershipLabel as Record<string, string>)[state] ?? DASH
}

const validationCheckLabel: Record<NodeValidationCheckCode, string> = {
  iommu_enabled: 'IOMMU',
  vfio_loaded: 'GPU 격리',
  gpu_binding: 'GPU 연결',
  boot_disk: '부팅 디스크',
  management_nic: '관리 네트워크',
  ntp_synchronized: '시간 동기화',
  pve_services: '가상화 서비스',
  pve_api: '장비 연결',
  pve_resources: '자원 확인',
  node_exporter: '사용량 수집',
  agent_report_timeout: '점검 응답',
  a5_sdn: '네트워크',
  a5_storage: '스토리지',
  a5_cluster_net: 'VM 망',
  a5_mock_vm: 'VM 생성',
  a5_start: '점검 시작',
}

/** 펌웨어의 일이라 시스템이 켤 수 없다 — 조치까지 한 문장. 노드 상세의 경고 아이콘(#249)도 이 문장을 쓴다. */
export const IOMMU_OFF_WORD = '장치 가상화(IOMMU)가 꺼져 있습니다. BIOS 에서 VT-d(Intel) 또는 AMD-Vi(AMD) 를 켜고 장비를 다시 부팅하세요.'

const validationCheckDescription: Record<NodeValidationCheckCode, string> = {
  iommu_enabled: IOMMU_OFF_WORD,
  vfio_loaded: 'GPU 격리가 준비되지 않았습니다.',
  gpu_binding: 'GPU 연결을 확인할 수 없습니다.',
  boot_disk: '부팅 디스크를 확인할 수 없습니다.',
  management_nic: '관리 네트워크를 확인할 수 없습니다.',
  ntp_synchronized: '장비 시간이 동기화되지 않았습니다.',
  pve_services: '가상화 서비스가 준비되지 않았습니다.',
  pve_api: '장비 관리 연결을 확인할 수 없습니다.',
  pve_resources: '장비 자원을 확인할 수 없습니다.',
  node_exporter: '사용량 수집이 준비되지 않았습니다.',
  agent_report_timeout: '장비 점검 응답이 없습니다.',
  a5_sdn: '네트워크 점검을 통과하지 못했습니다.',
  a5_storage: '스토리지 점검을 통과하지 못했습니다.',
  a5_cluster_net: 'VM 망 점검을 통과하지 못했습니다.',
  a5_mock_vm: 'VM 생성 점검을 통과하지 못했습니다.',
  a5_start: '장비 점검을 시작하지 못했습니다.',
}

/** 점검 주기가 지금 어느 대목인지. 코드는 백엔드의 것이고 낱말은 화면의 것이다. */
const validationStepLabel: Record<ValidationStep, string> = {
  waiting: '장비 점검 대기',
  checking: '장비 점검',
  qualifying: '클러스터 검증',
  retry_wait: '재시도 대기',
  done: '완료',
  failed: '실패',
}

export function validationStepWord(step: string): string {
  return (validationStepLabel as Record<string, string>)[step] ?? DASH
}

/** 실패 항목의 안정된 code도 화면에는 원문 대신 이 사전의 이름으로 보인다. */
export function validationCheckWord(code: string): string {
  return (validationCheckLabel as Record<string, string>)[code] ?? DASH
}

/** 백엔드 message를 옮기지 않고 check code별 고정 문장만 사용한다. */
export function validationCheckDescriptionWord(code: string): string {
  return (validationCheckDescription as Record<string, string>)[code] ?? DASH
}

/** 우리가 생성하는 제한된 형식만 제품 낱말로 바꾸고, 다른 원문은 화면 경계에서 버린다. */
export function validationObservedWord(code: string, observed: string | null | undefined): string | null {
  if (!observed) return null
  const known: Record<string, string> = {
    active: '정상',
    inactive: '중지',
    true: '예',
    false: '아니오',
    mounted: '연결됨',
    unavailable: '사용 불가',
  }
  const knownWord = known[observed]
  if (typeof knownWord === 'string') return knownWord

  if (code === 'gpu_binding') {
    const inventory = /^count=(\d+);inventory=([0-9a-f]{16})$/i.exec(observed)
    const count = inventory ? safeCount(inventory[1]) : null
    if (inventory && count !== null) return `GPU ${count}장 · 구성 ${inventory[2]!.toLowerCase()}`
  }
  if (code === 'management_nic') {
    const addresses = /^addresses=(\d+)$/.exec(observed)
    const count = addresses ? safeCount(addresses[1]) : null
    if (count !== null) return `주소 ${count}개`
  }
  if (code === 'pve_resources') {
    const nodes = /^nodes=(\d+)$/.exec(observed)
    const count = nodes ? safeCount(nodes[1]) : null
    if (count !== null) return `노드 ${count}대`
  }
  if (code.startsWith('a5_')) {
    const a5: Record<string, string> = {
      pending: '대기',
      running: '점검 중',
      passed: '정상',
      failed: '점검 실패',
      skipped: '대상 아님',
    }
    const a5Word = a5[observed]
    if (typeof a5Word === 'string') return a5Word
  }
  return null
}

function safeCount(value: string | undefined): number | null {
  if (!value) return null
  const count = Number(value)
  return Number.isSafeInteger(count) ? count : null
}

/** 온보딩의 여섯 국면 — 소속 결정과 실제 참여를 하나의 조인 단계로 보인다. */
export type Stage = '설치 중' | '첫 부팅' | 'GPU 격리' | '클러스터 연결' | '검증' | '사용 가능'

export const STAGES: readonly Stage[] = [
  '설치 중',
  '첫 부팅',
  'GPU 격리',
  '클러스터 연결',
  '검증',
  '사용 가능',
]

/** 대분류 코드를 그 낱말로. 접는 일은 백엔드가 한다(A6-17) — 화면은 옮기기만 한다. */
const STAGE_WORD: Record<NodeStage, Stage> = {
  install: '설치 중',
  first_boot: '첫 부팅',
  gpu_isolation: 'GPU 격리',
  cluster_join: '클러스터 연결',
  verification: '검증',
  ready: '사용 가능',
}

export function stageWord(stage: NodeStage): Stage {
  return STAGE_WORD[stage]
}

/**
 * 중분류 — 노드가 지금 하는 일. 낱말은 **스스로 설명해야 한다**: 노드 카드에는
 * 이 한 줄만 서고 대분류가 곁에 없다.
 */
const STEP_WORD: Record<NodeStepName, string> = {
  install: 'Proxmox 설치',
  first_boot: '장비 확인',
  gpu_inventory: 'GPU 확인',
  node_exporter: '사용량 수집기 설치',
  pve_token: '관리 자격 발급',
  storage_pools: '저장장치 준비',
  vfio_write: 'GPU 격리 설정',
  boot_params: '부팅 설정',
  vfio_bind: 'GPU 연결',
  gpu_scan: 'GPU 확인',
  cluster_wait: '클러스터 결정 대기',
  cluster_join: '클러스터 연결',
  validation: '장비 점검',
  verify_sdn: '네트워크 검증',
  verify_storage: '스토리지 검증',
  verify_data_pools: '데이터 풀 검증',
  verify_cluster_net: 'VM 망 검증',
  verify_mock_vm: 'VM 생성 시험',
}

export function stepWord(step: NodeStepName): string {
  return STEP_WORD[step] ?? DASH
}

/** 설치기의 줄이 이보다 오래 없으면 설치기가 말을 멈춘 것이다(#445). 줄은 설치 중 수 초 간격으로 온다. */
export const INSTALLER_QUIET_MS = 2 * 60 * 1000

/**
 * 지금 무슨 작업을 하는 중인가 — 진행 줄의 둘째 줄이자 노드 카드의 한 줄(A6-17).
 * 중분류 낱말 · 목록을 훑는 걸음이면 `7/12` · 지금 다루는 것 · 그 걸음에 머문 시간.
 *
 * 지어낸 퍼센트는 두지 않는다. 사람이 정하는 것은 "기다릴까, 가 볼까" 이고 그 답은
 * 셈이 준다 — `7/12` 는 남은 것이 다섯이라고 말하지만 58% 는 아무 말도 안 한다.
 * 설치기가 스스로 말한 진행률과 마지막 줄은 그대로 보인다(#445).
 */
export function doing(p: NodeProgress, now: number): string {
  // 닿지 않는 노드는 그 걸음을 하고 있다고 말할 수 없다(#489).
  let head = p.state === 'unreachable' ? '닿지 않음' : stepWord(p.step)
  if (typeof p.done === 'number' && typeof p.total === 'number') {
    head += ` ${p.done}/${p.total}`
  } else if (typeof p.attempt === 'number') {
    head += ` ${p.attempt}번째`
  } else if (typeof p.percent === 'number') {
    head += ` ${p.percent}%`
  }
  const parts = [head]
  if (p.item) parts.push(p.item)
  if (p.said) {
    // 진행률 줄은 비율을 머리에 이미 말했으니 글만 남긴다.
    parts.push(p.said.replace(/^.*?progress\s+[0-9.]+\s*%\s*-\s*/, ''))
  }
  const been = elapsed(p.since, now)
  if (been !== DASH) parts.push(`${been}째`)
  if (p.said_at && now - Date.parse(p.said_at) > INSTALLER_QUIET_MS) {
    parts.push(`마지막 보고 ${elapsed(p.said_at, now)} 전`)
  }
  return parts.join(' · ')
}

/**
 * 노드가 온보딩의 어디쯤인가. 실패했거나 점검 중인 노드는 국면이 아니라 상태 라벨로 말한다.
 * heartbeat 가 없는 구간은 재부팅이다: 설치 완료 뒤 첫 부팅 전은 아직 설치 중이고,
 * GPU 격리 뒤 장비 점검 전은 점검 중이다.
 */
export function stage(node: ApiNode): Stage | string {
  // 백엔드가 국면을 정한다(A6-17) — 상태만으로는 몇 번째 점검 시도인지, 어느
  // mock VM 이 떠 있는지 알 수 없다.
  if (node.progress) return stageWord(node.progress.stage)
  switch (node.state) {
    case 'enrolling':
      return '설치 중'
    case 'installed':
      return node.online ? '첫 부팅' : '설치 중'
    case 'vfio_configured':
      return node.online ? 'GPU 격리' : '점검 중'
    case 'gpu_verified':
    case 'joining':
      return '클러스터 연결'
    case 'joined':
    case 'burn_in':
      return '검증'
    case 'ready':
      return '사용 가능'
    default:
      return statusLabel[status(node)]
  }
}

/**
 * 마지막 부팅이 이 카드와 어떻게 끝났나(#401). 온보딩의 자취이지 카드의 건강이 아니다 —
 * `suspect` 은 이 카드를 vfio 에 붙이는 일이 부팅을 끝냈다는 뜻이고, 그래서 임대에서 빠진다.
 * 아직 닿은 적 없는 카드에는 할 말이 없다.
 */
const bindLabel: Record<GpuBind, string> = {
  bound: '연결됨',
  failed: '연결 실패',
  suspect: '이 카드에서 멈춤',
}

export function gpuBindWord(bind: GpuBind | undefined): string | null {
  return bind ? bindLabel[bind] : null
}

/**
 * 제외된 카드가 왜 임대에서 빠졌나(#322). 노드는 그 카드 없이 계속 가고, 카드만 남는다 —
 * 그래서 '임대 불가' 대신 이유를 한 낱말로 말한다. 사전에 없는 값은 대시다.
 */
const exclusionLabel: Record<GpuExclusion, string> = {
  iommu_group_shared: 'IOMMU 그룹 공유',
  iommu_group_unknown: '그룹 정보 없음',
  boot_vga_console: '호스트 화면용',
  not_vfio_bound: 'vfio 미연결',
  suspected_host_crash: '부팅 중단 의심',
}

export function gpuExclusionWord(exclusion: GpuExclusion | null | undefined): string | null {
  return exclusion ? (exclusionLabel[exclusion] ?? DASH) : null
}

/**
 * 지난 부팅이 어떻게 끝났나(A6-16). 고급 엔지니어가 먼저 알고 싶은 한 낱말이다 —
 * 종료 절차가 저널에 있으면 정상 종료이고, 그냥 끊겼으면 갑자기 멈춘 것이다.
 */
const bootEndedLabel: Record<NodeBootLog['ended'], string> = {
  clean: '정상 종료',
  abrupt: '갑자기 멈춤',
  unknown: '기록 없음',
}

export function bootEndedWord(ended: NodeBootLog['ended']): string {
  return bootEndedLabel[ended] ?? DASH
}

export function bootEndedTone(ended: NodeBootLog['ended']): Tone {
  return ended === 'abrupt' ? 'danger' : 'none'
}

/** 화면에 쓰는 제품 이름. PVE 이름·FQDN·uuid 는 대체 이름으로도 쓰지 않는다. */
export function nodeName(node: ApiNode): string {
  const name = (node.display_name ?? '').trim()
  return name || `노드 ${node.seq}`
}

/**
 * make test-db 가 개발 DB 에 남기는 행(#109).
 * 실제로 설치된 노드는 A-2 웹훅에서 pve_node_name 과 mgmt_ip 를 함께 받으므로
 * installed 인데 둘 다 비어 있을 수 없다.
 */
export function isTestArtifact(node: ApiNode): boolean {
  return node.state === 'installed' && !node.pve_node_name && !node.mgmt_ip
}

/**
 * GPU 한 장이 지금 임대에 나갈 수 있는가. 정의는 카탈로그가 남은 장 수로 세는 것과 같아야 한다
 * (CountFreeLeasableGPUsByModelOnRecord 의 FILTER): state=free + 아는 모델 + pve_mapping_id + 배치가 받는 노드.
 * 모델을 아는가는 `vram_mb` 로 본다 — 둘은 GPU 카탈로그의 같은 행에서 함께 적힌다.
 * 배치가 받는 노드는 상태가 사용 가능인 노드다 — 점검 중·오프라인·보고가 끊긴 노드(#503)의 카드는 아무도 받지 못한다.
 */
export function gpuUsable(node: ApiNode, gpu: Gpu): boolean {
  return gpu.state === 'free' && gpu.vram_mb !== null && gpu.pve_mapping_id !== null && status(node) === 'ready'
}

/**
 * 노드 상세의 카드 배지 낱말. 카드 자신의 사정(고장·임대 중·제외 사유)이 먼저이고,
 * 비어 있는 카드라도 노드가 배치에서 빠졌으면 `사용 불가` 다 — 이유는 노드의 상태와 제목 줄이 말한다.
 */
export function gpuWord(node: ApiNode, gpu: Gpu): { word: string; tone: 'ok' | 'danger' | 'muted' } {
  if ((node.excluded_gpus ?? []).includes(gpu.pci_addr)) return { word: '고장', tone: 'danger' }
  if (gpuUsable(node, gpu)) return { word: '사용 가능', tone: 'ok' }
  if (gpu.state === 'allocated') return { word: '임대 중', tone: 'muted' }
  const excluded = gpuExclusionWord(gpu.exclusion)
  if (excluded) return { word: excluded, tone: 'muted' }
  if (gpu.state === 'free' && status(node) !== 'ready') return { word: '사용 불가', tone: 'muted' }
  return { word: '임대 불가', tone: 'muted' }
}

/** 노드가 어떻게 등록됐는가. 원값은 화면에 내지 않는다. */
export function nodeOriginWord(origin: ApiNode['origin']): string {
  return origin === 'adopted' ? '편입' : '설치'
}

/**
 * 관리 이미지 사본 한 줄(#331): 레시피 · 상태. 새 VM 이 쓰지 않는 레시피면 `옛 판`.
 * 상태 낱말은 설치 ISO 의 것을 그대로 쓴다 — 둘 다 백엔드가 만들어 두는 파일이다.
 */
export function imageCopyText(copy: NodeImageCopy): string {
  const word = ISO_WORD[copy.state]?.label ?? DASH
  return `${copy.source_key} · ${word}${copy.current ? '' : ' · 옛 판'}`
}

/** 장비 점검의 범위. */
export function validationScopeWord(scope: NodeValidation['scope']): string {
  switch (scope) {
    case 'initial':
      return '첫 점검'
    case 'reboot':
      return '재부팅'
    case 'maintenance':
      return '점검 복귀'
    case 'resume':
      return '복귀 예약'
  }
}

/** 최근 점검에서 IOMMU 가 꺼진 것으로 나왔는가 — 노드 목록·상세의 경고 아이콘이 서는 조건. 입력은 실패한 항목 코드(`Node.failed_checks`). */
export function iommuOff(failedChecks: readonly string[]): boolean {
  return failedChecks.includes('iommu_enabled')
}

/** 노드 카드 아이콘의 색 — 정상 초록 · 준비 중 파랑(브랜드) · 오프라인·실패 빨강 · 중지·대기 노랑. 배지(`StatusBadge`)와 같은 판정이고 아이콘에만 파랑이 있다. */
export const ICON_TONE: Record<Status, string> = {
  ready: 'text-ok',
  paused: 'text-warn',
  failed: 'text-danger',
  preparing: 'text-brand',
  offline: 'text-danger',
  awaiting: 'text-warn',
}

/** A-5 검증의 단계 낱말(#326). 단계 원값(sdn·mock_vm)은 화면에 내지 않는다. */
const VERIFY_STEP_WORD: Record<VerifyStepName, string> = {
  sdn: '게스트 망',
  storage: '스토리지',
  data_pools: '데이터 풀',
  cluster_net: 'VM 망',
  mock_vm: 'GPU 확인',
}

export const VERIFY_STEP_ORDER: VerifyStepName[] = ['sdn', 'storage', 'data_pools', 'cluster_net', 'mock_vm']

export function verifyStepWord(step: VerifyStepName): string {
  return VERIFY_STEP_WORD[step] ?? DASH
}

/**
 * 실패한 단계의 막힘 한 문장(#326). 사람이 고칠 것이 있으면 무엇을 볼지까지,
 * 없으면 검증이 스스로 다시 돈다는 것까지 — 원문 오류는 옮기지 않는다.
 */
const VERIFY_FAILURE: Record<VerifyFailureCode, string> = {
  no_cluster_access: '클러스터에 접근하지 못했습니다. 클러스터의 접근 자격을 확인하세요.',
  read_failed: '클러스터가 응답하지 않았습니다. 스스로 다시 검증합니다.',
  zones_not_applied: '게스트 망 설정이 이 노드에 적용되지 않았습니다. 클러스터의 게스트 망 설정을 확인하세요.',
  shared_storage_inactive: '공유 스토리지가 이 노드에 연결되지 않았습니다. 노드에서 공유 스토리지에 닿는지 확인하세요.',
  data_pool_unhealthy: '데이터 풀 디스크가 정상이 아닙니다. 디스크를 확인하세요.',
  data_pool_storage_missing: '데이터 풀이 VM 디스크를 담을 수 있게 등록되어 있지 않습니다. 노드 이벤트에서 원인을 확인하세요.',
  gpu_mapping_missing: 'GPU 를 VM 에 연결할 준비가 되지 않았습니다. 스스로 다시 검증합니다.',
  image_not_ready: 'VM 이미지가 아직 이 노드에 준비되지 않았습니다. 준비되면 스스로 다시 검증합니다.',
  vm_storage_missing: '이 노드에 VM 디스크를 둘 스토리지가 없습니다. 노드의 스토리지를 확인하세요.',
  vm_failed: '시험 VM 을 만들지 못했습니다. 스스로 다시 검증합니다.',
  guest_no_response: '시험 VM 이 부팅하지 않았습니다. GPU 와 BIOS 설정을 확인하세요.',
  gpu_not_visible: 'VM 에서 GPU 가 보이지 않습니다. GPU 와 BIOS 설정을 확인하세요.',
  cleanup_failed: '시험 VM 을 지우지 못했습니다. 노드 이벤트에서 남은 VM 을 확인하세요.',
  cluster_net_unreported: '노드가 VM 망 상태를 보고하지 않았습니다. 스스로 다시 검증합니다.',
  cluster_net_not_applied: '이 노드의 VM 망을 설정하지 못했습니다. 스스로 다시 검증합니다.',
  cluster_net_unreachable: '다른 노드의 VM 망에 닿지 않습니다. 노드들이 관리 네트워크에서 서로 닿는지 확인하세요.',
  timed_out: '검증이 제한 시간 안에 끝나지 않았습니다. 스스로 다시 검증합니다.',
  interrupted: '검증이 중간에 멈췄습니다. 스스로 다시 검증합니다.',
}

/** 코드가 생기기 전의 실패 — 이유를 모른다고 말한다. */
export const VERIFY_FAILED_WORD = '통과하지 못했습니다.'

export function verifyFailureSentence(code: VerifyFailureCode | null | undefined): string {
  return (code && VERIFY_FAILURE[code]) || VERIFY_FAILED_WORD
}

export type VerifyRow = {
  step: VerifyStepName
  /** 정상 · 해당 없음 · 진행 중 · 막힘 한 문장 */
  result: string
  failed: boolean
  /** 실패한 카드의 PCI 주소(GPU 확인). 고급에서만 보인다. */
  items: string[]
}

/**
 * 검증 한 번을 단계마다 한 줄로(#326). GPU 확인은 카드마다 행이 있어 한 줄로 묶고,
 * 여러 장 가운데 일부만 막혔으면 몇 장인지 붙인다.
 */
export function verifyRows(run: VerifyRun): VerifyRow[] {
  const rows: VerifyRow[] = []
  for (const step of VERIFY_STEP_ORDER) {
    const mine = run.steps.filter((s) => s.name === step)
    if (mine.length === 0) continue
    const failed = mine.filter((s) => s.state === 'failed')
    let result: string
    if (failed.length > 0) {
      result = verifyFailureSentence(failed[0]!.code)
      if (mine.length > 1) result = `${mine.length}장 중 ${failed.length}장 · ${result}`
    } else if (mine.some((s) => s.state === 'pending' || s.state === 'running')) {
      result = '진행 중'
    } else if (mine.every((s) => s.state === 'skipped')) {
      result = '해당 없음'
    } else {
      result = '정상'
    }
    const items = failed.map((s) => s.detail?.pci_addr).filter((a): a is string => typeof a === 'string')
    rows.push({ step, result, failed: failed.length > 0, items })
  }
  return rows
}
